import type { TFunction } from 'i18next';
import { Edit } from 'qampo-ui-library';
import TrashIcon from '../../assets/icons/trash.svg?react';
import { IconButton } from '../UI';
import type { TableColumn } from '../UI';
import { getLocationColor } from '../../constants/locationColors';
import { countAssignedUsers } from '../../services/locationUserService';
import type { LocationSettingsRow } from '../../hooks/useLocationsSettings';
import { LOCATION_COLUMN_WIDTHS } from './locationsConstants';
import styles from './LocationsSettings.module.css';

interface LocationColumnHandlers {
  onEdit: (location: LocationSettingsRow) => void;
  onDelete: (location: LocationSettingsRow) => void;
}

export const getLocationColumns = (
  t: TFunction,
  { onEdit, onDelete }: LocationColumnHandlers
): TableColumn<LocationSettingsRow>[] => [
  {
    key: 'name',
    header: t('config-loc-col-name'),
    sortable: true,
    sortValue: (location) => location.name,
    // The wrapper is what gives the name its truncation: nameText ellipsises
    // only inside a flex parent with min-width 0.
    render: (location) => (
      <span className={styles.nameCell}>
        <span className={styles.nameText}>{location.name}</span>
      </span>
    )
  },
  {
    key: 'color',
    header: t('config-loc-col-color'),
    width: LOCATION_COLUMN_WIDTHS.color,
    // Dot only, no colour name, matching how the treatment plan builder shows a
    // template's colour.
    render: (location) => {
      const color = getLocationColor(location.colorId);
      return (
        <span
          className={styles.swatch}
          style={{ backgroundColor: color.base }}
          title={t(color.labelKey)}
        />
      );
    }
  },
  {
    key: 'users',
    header: t('config-loc-col-users'),
    width: LOCATION_COLUMN_WIDTHS.users,
    sortable: true,
    sortValue: (location) => countAssignedUsers(location.userRoles),
    render: (location) => (
      <span className={styles.mutedCell}>
        {t('config-loc-user-count', { count: countAssignedUsers(location.userRoles) })}
      </span>
    )
  },
  {
    key: 'actions',
    header: '',
    width: LOCATION_COLUMN_WIDTHS.actions,
    headerAlign: 'center',
    // Names both actions per row, so they announce as "Edit Sano Aarhus" rather
    // than two indistinguishable "button"s.
    render: (location) => (
      <div className={styles.rowActions}>
        <IconButton
          size="small"
          icon={<Edit width={14} height={14} />}
          onClick={() => onEdit(location)}
          ariaLabel={t('config-loc-edit-aria', { name: location.name })}
          className={styles.actionBtn}
        />
        <IconButton
          size="small"
          icon={<TrashIcon width={14} height={14} />}
          onClick={() => onDelete(location)}
          ariaLabel={t('config-loc-delete-aria', { name: location.name })}
          className={`${styles.actionBtn} ${styles.actionBtnDelete}`}
        />
      </div>
    )
  }
];
